import { ChangeDetectionStrategy, Component, ElementRef, Host, Input, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { DAYS, GasDatepicker, GasDatepickerTime, GasPickerDay, MONTH } from './gas-datepicker.interface';
import { GasDatepickerHelper } from './gas-datepicker-helper';
import { GasClickOutsideDirective } from '../../utils/click-outside-directive/gas-click-outside.directive';
import { GasInputBoolean } from '@cikrf/gas-utils/decorators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/** @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=589%3A5596 */
@UntilDestroy()
@Component({
  selector: 'gas-datepicker',
  templateUrl: './gas-datepicker.component.html',
  styleUrls: ['./gas-datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GasClickOutsideDirective,
  ],
})
/** @deprecated */
export class GasDatepickerComponent implements OnInit {
  @ViewChild('datepickerWrapper', {static: true})
  public datepickerWrapper: ElementRef;

  /** флаг для отображения поля ввода времени */
  @GasInputBoolean()
  @Input()
  public timesPicker = false;

  @Input()
  public defaultTime: GasDatepickerTime;

  /** Текущий выбранный день */
  @Input()
  public set presetDate(presetDate: Date) {
    if (presetDate) {
      this.selectedDateSubject$.next(presetDate);
      this.updateDate();
    }
  }

  /** Возвращает выбранную дату */
  public get presetDate(): Date {
    return this.selectedDateSubject$.getValue();
  }

  /** Массив с днями для рендера в pickere */
  public daysInMonth: GasPickerDay[] = [];
  /** массив с днями недели */
  public weeks: string[] = DAYS;
  /** массив с месяцами */
  public month: string[] = MONTH;
  /** минимально доступный для выбора день */
  public minDate: Date;
  /** максимально доступный для выбора день */
  public maxDate: Date;
  /** Объект хранящий актуальные значения даты */
  public dateSetting: GasDatepicker;
  /** Показывает календарь/список годов/список месяцев */
  public currentViewPicker: 'datepicker' | 'yearpicker' | 'monthpicker' = 'datepicker';
  /** Выбранная дата */
  public selectedDateSubject$: BehaviorSubject<Date> = new BehaviorSubject(null);
  /** Храним состояние клика для удаления компонента */
  public closeSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  /** инстанс класса-хелпера */
  public gasDatepickerHelper = new GasDatepickerHelper();

  constructor(
    @Host() private gasClickOutsideDirective: GasClickOutsideDirective,
    public el: ElementRef,
  ) { }

  /**************** Геттеры ***********************/

  /** Возвращает текущий месяц */
  public get currentMonth(): number {
    return this.dateSetting.currentMonth;
  }

  /** Возвращает текущий год */
  public get currentYear(): string {
    return this.dateSetting.currentYear.toString();
  }

  /**
   * Инициализация при загрузке страницы
   * Добавляем подписку на клик вне компонента, для удаления динамического компонента
   */
  public ngOnInit(): void {
    this.updateDate();
    this.gasClickOutsideDirective.handleClick$
      .pipe(
        tap(click => this.closeSubject$.next(!!click)),
        untilDestroyed(this),
      ).subscribe();
  }

  /******************* Методы по установке даты ************************************/

  /**
   * Выбираем день в календаре
   * Вначале проверяем, относится ли выбранный день к текущему месяцу, если нет - то меняем месяц
   * Если день из текущего месяца то просто устанавливаем его как выбранный
   *
   * @param day - выбранный день из календаря
   */
  public selectDay(day: GasPickerDay): void {
    if (!day.isCurrent) {
      return;
    }

    if (day.otherMonth) {
      this.dateSetting.currentMonth += day.monthIndex;
      this.checkAndSetMonth();
    }

    if (this.timesPicker) {
      this.changeRenderPicker(day.value);
      return;
    }

    if (this.gasDatepickerHelper.checkToDay(day.value)) {
      this.setToDay();
      return;
    }

    this.presetDate = new Date(this.dateSetting.currentYear, this.dateSetting.currentMonth, day.value.getDate());

    this.closeSubject$.next(true);
  }

  /**
   * Устанавливает дату на текущий день
   */
  public setToDay(): void {
    this.presetDate = new Date();
    this.closeSubject$.next(true);
  }

  /** Обновляет дату в директиве, изменяет массив с датами и закрывает picker */
  public updateDate(): void {
    this.dateSetting = this.gasDatepickerHelper.createCurrentDateObject(this.presetDate ?? undefined);
    this.daysInMonth = [];
    this.changeRenderPicker(this.presetDate ?? undefined);
  }

  /**
   * Обновляет данные в календаре и объекте с актуальными значениями даты
   *
   * @param day - текущий выбранный день
   */
  public changeRenderPicker(day: Date = new Date()): void {
    this.dateSetting = this.gasDatepickerHelper.createCurrentDateObject(day, this.dateSetting.currentMonth, this.dateSetting.currentYear);
    this.daysInMonth = this.gasDatepickerHelper.setDaysInMonth(this.dateSetting, this.minDate, this.maxDate);
  }

  /******************* Методы для работы со сменой месяцев и года ********************************/

  /** Выбираем предыдущий месяц */
  public previousMonth(index: number): void {
    this.changeMonth(index);
  }

  /** Выбираем следующий месяц */
  public nextMonth(index: number): void {
    this.changeMonth(index);
  }

  /**
   * Меняет текущий месяц
   *
   * @param index - индекс выбранного месяца
   */
  public changeMonth(index: number): void {
    this.dateSetting.currentMonth += index;
    this.checkAndSetMonth();
    this.changeRenderPicker(this.presetDate ?? undefined);
  }

  /**
   * проверяет текущий месяц и меняет в нем значение если он выходит за корректный диапазон
   * в случае выхода за диапазон еще меняет год
   */
  public checkAndSetMonth(): void {
    if (this.dateSetting.currentMonth < 0) {
      this.dateSetting.currentMonth = 11;
      this.dateSetting.currentYear -= 1;
    } else if (this.dateSetting.currentMonth > 11) {
      this.dateSetting.currentMonth = 0;
      this.dateSetting.currentYear += 1;
    }
  }

  /** открывает блок с выбором года */
  public openYearPicker(): void {
    this.currentViewPicker = 'yearpicker';
  }

  /** открывает блок с выбором месяца */
  public openMonthPicker(): void {
    this.currentViewPicker = 'monthpicker';
  }

  /** обрабатываем изменение года */
  public onChangeYear(year: number): void {
    this.currentViewPicker = 'datepicker';
    this.dateSetting.currentYear = year;
    this.changeRenderPicker(this.presetDate ?? undefined);
  }

  /** обрабатываем изменение месяца из отдельного блока */
  public changeCurrentMonth(month: number): void {
    this.currentViewPicker = 'datepicker';
    this.dateSetting.currentMonth = month;
    this.changeRenderPicker(this.presetDate ?? undefined);
  }

  //************************** Метода из дочерних компонентов ********************************/

  public closePicker(): void {
    this.closeSubject$.next(true);
  }

  public setDateAndTime(times: string[]): void {
    this.presetDate = new Date(
      this.dateSetting.currentYear,
      this.dateSetting.currentMonth,
      this.dateSetting.currentDay.getDate(),
      times[0] ? +times[0] : this.defaultTime.hours,
      times[1] ? +times[1] : this.defaultTime.minutes,
      times[2] ? +times[2] : this.defaultTime.seconds,
    );

    this.closePicker();
  }
}
